import { useMemo, useState, FC } from "react";

type Prop = {
  className?: string;
  value?: number;
  onChange?(value: number | null): void;
  placeholder?: string;
};

const formatOptions: Intl.DateTimeFormatOptions = {
  minute: "2-digit",
  second: "2-digit",
  timeZone: "UTC", // 時刻ではなく再生時間のフォーマットに使いたいのでタイムゾーン調整しないように。
};
/** e.g. `12:34` */
const formatter = Intl.DateTimeFormat("ja-JP", formatOptions);
/** e.g. `12:34:56` */
const formatterWithHour = Intl.DateTimeFormat("ja-JP", {
  ...formatOptions,
  hour: "numeric",
});

/**
 * Format seconds to time notation.
 * @param {number} timeSec
 * @returns {string} `12:34` or `12:34:56` or null
 */
export function formatTimeSec(timeSec: number): string | null {
  if (Number.isFinite(timeSec)) {
    const f = timeSec >= 3600 ? formatterWithHour : formatter;
    return f.format(timeSec * 1000);
  }
  return null;
}

/** `0:12` or `12:34` or `12:34:56` */
const TIME_SEC_REGEXP = /^(?:(\d+):)?([0-5]?\d):([0-5]\d)$/;

function parse(text: string): number | null {
  const match = (text ?? "").trim().match(TIME_SEC_REGEXP);
  if (match) {
    const [, hour, min, sec] = [...match];
    const timeSec = Number(hour || 0) * 3600 + Number(min) * 60 + Number(sec);
    if (Number.isSafeInteger(timeSec)) {
      return timeSec;
    }
  }
  return null;
}

/**
 * 秒数を「hh:mm:ss」の書式のテキストで扱うテキストフィールド。
 * TimeMsInputと同様の機能を持つが、ミリ秒ではなく秒数を扱うので、別実装にする。
 *
 * 適切な入力の時は値をonChangeで親コンポーネントに戻し、propsと同期する。
 * 不適切な入力の時はnullを返し、適切な入力になるまではpropsと同期せず、このコンポーネント内だけでinputを制御する。
 */
export const TimeSecInput: FC<Prop> = (props) => {
  const [text, setText] = useState(() => formatTimeSec(props.value));
  const propValue = useMemo(() => formatTimeSec(props.value), [props.value]);

  function addition(timeSec: number): void {
    const newTimeSec = (parse(text) ?? 0) + timeSec;
    if (newTimeSec < 0) {
      return; // 負の値は許容しない
    }
    setText(formatTimeSec(newTimeSec));
    props.onChange(newTimeSec);
  }

  return (
    <input
      type="text"
      pattern={TIME_SEC_REGEXP.source}
      className={props.className}
      value={propValue ?? text ?? ""}
      onChange={(event) => {
        setText(event.target.value);
        const timeMs = parse(event.target.value);
        if (props.value !== timeMs) {
          props.onChange(timeMs);
        }
      }}
      placeholder={props.placeholder}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing) {
          return; // IME入力中は無視
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          addition(1);
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          addition(-1);
        }
      }}
    />
  );
};
